import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Store, Plus, Users, Package, ShoppingCart, Building2, TrendingUp, Settings, Loader2, CalendarClock, CreditCard, FileText, Save, BarChart3, Mail, Send, Eye, EyeOff, Code, Upload, Image, Palette, RefreshCcw, Copy, CheckCircle2, Key, Trash2 } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function StoreManagement() {
  const { darkMode } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteTestTarget, setDeleteTestTarget] = useState(null);
  const [deletingTest, setDeletingTest] = useState(false);
  const [showDeleteAllTest, setShowDeleteAllTest] = useState(false);
  const [deletingAllTest, setDeletingAllTest] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeStats, setStoreStats] = useState({});
  const [planLimits, setPlanLimits] = useState({});
  const [activeTab, setActiveTab] = useState("stores");

  // Fiscal data
  const [fiscalData, setFiscalData] = useState({});
  const [savingFiscal, setSavingFiscal] = useState(false);
  const [uploadingInvoiceLogo, setUploadingInvoiceLogo] = useState(false);

  // Payments
  const [allPayments, setAllPayments] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [paymentsSummary, setPaymentsSummary] = useState({});
  const [loadingPayments, setLoadingPayments] = useState(false);

  // Email templates
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsPage, setEmailLogsPage] = useState(1);
  const [emailLogsTotal, setEmailLogsTotal] = useState(0);
  const [emailLogsTotalPages, setEmailLogsTotalPages] = useState(1);
  const [emailLogsSearch, setEmailLogsSearch] = useState("");
  const [loadingEmailLogs, setLoadingEmailLogs] = useState(false);
  const [resendingLogId, setResendingLogId] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [templateLogo, setTemplateLogo] = useState(null);

  // New store form
  const [newStore, setNewStore] = useState({
    name: "",
    plan: "trial",  // Default to trial for new stores
    max_users: 10,
    max_items: 10000,
    max_customers: 10000,
    contact_email: "",
    contact_phone: "",
    address: ""
  });
  const [createdCredentials, setCreatedCredentials] = useState(null);

  // Verificar permisos de super_admin
  useEffect(() => {
    if (user && user.role !== "super_admin") {
      toast.error("Esta página es solo para administradores del sistema");
      navigate("/");
      return;
    }
    if (user) {
      loadStores();
      loadPlanLimits();
      loadFiscalData();
    }
  }, [user, navigate]);

  const loadFiscalData = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/fiscal-data`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setFiscalData(res.data);
    } catch (e) {
      console.error("Error loading fiscal data:", e);
    }
  };

  const saveFiscalData = async () => {
    setSavingFiscal(true);
    try {
      const res = await axios.put(`${API}/api/admin/fiscal-data`, fiscalData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setFiscalData(res.data);
      toast.success("Datos fiscales guardados");
    } catch (e) {
      toast.error("Error al guardar datos fiscales");
    } finally {
      setSavingFiscal(false);
    }
  };

  const handleInvoiceLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingInvoiceLogo(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);
      const res = await axios.post(`${API}/api/admin/fiscal-data/upload-logo`, formData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }
      });
      setFiscalData(prev => ({ ...prev, invoice_logo: res.data.logo_url }));
      toast.success("Logo de factura subido correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir el logo");
    } finally {
      setUploadingInvoiceLogo(false);
    }
  };

  const loadPayments = async () => {
    setLoadingPayments(true);
    try {
      const res = await axios.get(`${API}/api/admin/payments`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setAllPayments(res.data.payments || []);
      setMonthlyStats(res.data.monthly_stats || []);
      setPaymentsSummary(res.data.summary || {});
    } catch (e) {
      console.error("Error loading payments:", e);
    } finally {
      setLoadingPayments(false);
    }
  };

  const loadEmailLogs = async (page = 1, search = emailLogsSearch) => {
    setLoadingEmailLogs(true);
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const params = new URLSearchParams({ page: String(page), page_size: "25" });
      if (search && search.trim()) params.set("recipient", search.trim());
      const res = await axios.get(`${API}/api/admin/email-logs?${params.toString()}`, { headers });
      setEmailLogs(res.data.logs || []);
      setEmailLogsPage(res.data.page || page);
      setEmailLogsTotal(res.data.total || 0);
      setEmailLogsTotalPages(res.data.total_pages || 1);
    } catch (e) {
      console.error("Error loading email logs:", e);
    } finally {
      setLoadingEmailLogs(false);
    }
  };

  const loadEmailTemplates = async () => {
    setLoadingEmails(true);
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const tplRes = await axios.get(`${API}/api/admin/email-templates`, { headers });
      setEmailTemplates(tplRes.data.templates || []);
      await loadEmailLogs(1);
    } catch (e) {
      console.error("Error loading email templates:", e);
    } finally {
      setLoadingEmails(false);
    }
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;
    setSavingTemplate(true);
    try {
      await axios.put(`${API}/api/admin/email-templates/${editingTemplate.alias}`, {
        subject: editSubject,
        body: editBody,
        active: editActive,
      }, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      toast.success("Template guardada correctamente");
      setEditingTemplate(null);
      loadEmailTemplates();
    } catch (e) {
      toast.error("Error al guardar template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const sendTestEmail = async () => {
    if (!editingTemplate || !testEmail) return;
    setSendingTest(true);
    try {
      await axios.post(`${API}/api/admin/email-templates/${editingTemplate.alias}/test`, {
        recipient_email: testEmail,
      }, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      toast.success(`Email de prueba enviado a ${testEmail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar email de prueba");
    } finally {
      setSendingTest(false);
    }
  };

  const handleResendEmail = async (log) => {
    if (!log.log_id || resendingLogId) return;
    setResendingLogId(log.log_id);
    try {
      await axios.post(
        `${API}/api/admin/email-logs/${log.log_id}/resend`,
        {},
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(`✅ Reenviado correctamente a ${log.recipient}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reenviar el email");
    } finally {
      setResendingLogId(null);
    }
  };

  const openTemplateEditor = (tpl) => {
    setEditingTemplate(tpl);
    setEditSubject(tpl.subject);
    setEditBody(tpl.body);
    setEditActive(tpl.active !== false);
    setTemplateLogo(tpl.header_logo || null);
    setTestEmail("");
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingTemplate) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);
      const res = await axios.post(`${API}/api/admin/email-templates/${editingTemplate.alias}/upload-logo`, formData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }
      });
      setTemplateLogo(res.data.logo_url);
      toast.success("Logo subido correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir el logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const QUILL_MODULES = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean'],
    ],
  };

  const loadPlanLimits = async () => {
    try {
      const response = await axios.get(`${API}/api/plans`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setPlanLimits(response.data.plans);
      
      // Update newStore with trial plan limits
      if (response.data.plans.trial) {
        setNewStore(prev => ({
          ...prev,
          max_items: response.data.plans.trial.max_items,
          max_customers: response.data.plans.trial.max_customers,
          max_users: response.data.plans.trial.max_users
        }));
      }
    } catch (error) {
      console.error("Error loading plan limits:", error);
    }
  };

  const loadStores = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/stores`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setStores(response.data);
      
      // Load stats for each store
      for (const store of response.data) {
        loadStoreStats(store.store_id);
      }
    } catch (error) {
      toast.error("Error al cargar tiendas");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (storeId) => {
    try {
      // Cookie-only: el backend guarda el token del super_admin en la cookie
      // `sa_token` y sobrescribe `access_token` con el token de la tienda
      // impersonada. No tocamos localStorage.
      await axios.post(`${API}/api/stores/${storeId}/impersonate`, {});
      toast.success(`Accediendo a la tienda...`);

      // Full page reload para que todos los contextos se rehidraten con el
      // nuevo rol/tienda leído desde /auth/me.
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al acceder a la tienda");
      console.error(error);
    }
  };

  const handleToggleStatus = async (storeId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'activar' : 'desactivar';
    
    if (!confirm(`¿Estás seguro de que deseas ${action} esta tienda?`)) {
      return;
    }

    try {
      await axios.put(
        `${API}/api/stores/${storeId}`,
        { status: newStatus },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success(`Tienda ${action === 'activar' ? 'activada' : 'desactivada'} exitosamente`);
      loadStores(); // Reload stores
    } catch (error) {
      toast.error(`Error al ${action} la tienda`);
      console.error(error);
    }
  };

  const isTestStoreName = (name) => (name || "").trim().toUpperCase().startsWith("TEST");
  const testStores = stores.filter((s) => isTestStoreName(s.name) && !s.is_system_store);

  const deleteStoreRequest = (storeId, storeName) =>
    axios.delete(
      `${API}/api/stores/${storeId}?confirm_name=${encodeURIComponent(storeName)}`,
      { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
    );

  const handleDeleteStore = async (storeId, storeName, isSystemStore) => {
    // Extra safety net in frontend — the backend also enforces this via is_system_store flag
    if (isSystemStore) {
      toast.error("Esta es la tienda espejo del sistema y no puede ser eliminada.");
      return;
    }

    // Test stores (name starts with TEST): simple confirm dialog, no typing
    if (isTestStoreName(storeName)) {
      setDeleteTestTarget({ store_id: storeId, name: storeName });
      return;
    }

    const confirmText = `¿ELIMINAR ${storeName}?`;
    const userInput = prompt(`Esta acción es IRREVERSIBLE.\n\nSe eliminarán:\n- La tienda\n- Todos los usuarios asociados\n\nPara confirmar, escribe: ${confirmText}`);
    
    if (userInput !== confirmText) {
      toast.error("Cancelado - Texto de confirmación incorrecto");
      return;
    }

    try {
      const response = await deleteStoreRequest(storeId, storeName);
      
      toast.success(`Tienda eliminada: ${response.data.deleted_users} usuarios eliminados`);
      loadStores(); // Reload stores
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar la tienda");
      console.error(error);
    }
  };

  const confirmDeleteTestStore = async () => {
    if (!deleteTestTarget) return;
    setDeletingTest(true);
    try {
      await deleteStoreRequest(deleteTestTarget.store_id, deleteTestTarget.name);
      toast.success(`Tienda de prueba «${deleteTestTarget.name}» eliminada`);
      setDeleteTestTarget(null);
      loadStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar la tienda");
      console.error(error);
    } finally {
      setDeletingTest(false);
    }
  };

  const handleDeleteAllTestStores = async () => {
    setDeletingAllTest(true);
    try {
      const response = await axios.post(
        `${API}/api/stores/delete-test-stores`,
        {},
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(`${response.data.deleted_count} tiendas de prueba eliminadas`);
      setShowDeleteAllTest(false);
      loadStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error en el borrado en lote");
      console.error(error);
    } finally {
      setDeletingAllTest(false);
    }
  };

  const loadStoreStats = async (storeId) => {
    try {
      const response = await axios.get(`${API}/api/stores/${storeId}/stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setStoreStats(prev => ({ ...prev, [storeId]: response.data }));
    } catch (error) {
      console.error(`Error loading stats for store ${storeId}:`, error);
    }
  };

  const handleCreateStore = async () => {
    if (!newStore.name) {
      toast.error("El nombre de la tienda es obligatorio");
      return;
    }

    try {
      const res = await axios.post(`${API}/api/stores`, newStore, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setShowCreateDialog(false);
      
      // Show generated admin credentials
      if (res.data?.admin_credentials) {
        setCreatedCredentials(res.data.admin_credentials);
      } else {
        toast.success("Tienda creada exitosamente");
      }
      
      // Reset form with trial plan limits
      const trialLimits = planLimits.trial || {};
      setNewStore({
        name: "",
        plan: "trial",
        max_users: trialLimits.max_users || 10,
        max_items: trialLimits.max_items || 10000,
        max_customers: trialLimits.max_customers || 10000,
        contact_email: "",
        contact_phone: "",
        address: ""
      });
      loadStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear tienda");
      console.error(error);
    }
  };

  const getPlanBadge = (store) => {
    const planType = store.plan || store.plan_type || 'basic';

    if (store.subscription_state === 'trial_expired') {
      return (
        <Badge className="bg-red-600 text-white" data-testid={`store-badge-trial-expired-${store.store_id}`}>
          PRUEBA CADUCADA
        </Badge>
      );
    }
    if (store.subscription_state === 'cancelled') {
      return (
        <Badge className="bg-slate-600 text-white" data-testid={`store-badge-cancelled-${store.store_id}`}>
          CANCELADA
        </Badge>
      );
    }

    const colors = {
      basic: "bg-slate-500 text-white",
      pro: "bg-blue-600 text-white",
      enterprise: "bg-purple-600 text-white",
      trial: "bg-amber-500 text-white"
    };
    return (
      <Badge className={colors[planType] || colors.basic}>
        {planType.toUpperCase()}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    return status === "active" ? (
      <Badge className="bg-emerald-500 text-white">Activa</Badge>
    ) : (
      <Badge className="bg-red-500 text-white">Inactiva</Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={`p-6 lg:p-8 space-y-6 min-h-screen ${darkMode ? 'bg-[#121212]' : 'bg-slate-50'}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            <Building2 className="inline-block h-8 w-8 mr-2" />
            Gestión de Tiendas
          </h1>
          <p className={`mt-1 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
            Panel de Super Administrador - {stores.length} tienda{stores.length !== 1 ? 's' : ''} registrada{stores.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Credentials Dialog after store creation */}
      <Dialog open={!!createdCredentials} onOpenChange={(open) => { if (!open) setCreatedCredentials(null); }}>
        <DialogContent className="max-w-md" data-testid="credentials-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              Tienda creada correctamente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Se ha creado el usuario administrador para esta tienda. Comparte estas credenciales con el propietario:
            </p>
            <div className="bg-slate-50 border rounded-lg p-4 space-y-3 font-mono text-sm" data-testid="credentials-box">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs text-slate-500 font-sans">Usuario</p>
                  <p className="font-semibold text-slate-800">{createdCredentials?.username}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(createdCredentials?.username); toast.success("Usuario copiado"); }} data-testid="copy-username-btn">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs text-slate-500 font-sans">Contraseña inicial</p>
                  <p className="font-semibold text-slate-800">{createdCredentials?.password}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(createdCredentials?.password); toast.success("Contraseña copiada"); }} data-testid="copy-password-btn">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <Key className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">El propietario debería cambiar la contraseña tras el primer inicio de sesión.</p>
            </div>
            <Button className="w-full" onClick={() => setCreatedCredentials(null)} data-testid="close-credentials-btn">
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'payments' && allPayments.length === 0) loadPayments(); if (v === 'emails' && emailTemplates.length === 0) loadEmailTemplates(); }} className="space-y-4">
        <TabsList>
          <TabsTrigger value="stores"><Store className="h-4 w-4 mr-2" />Tiendas</TabsTrigger>
          <TabsTrigger value="fiscal"><FileText className="h-4 w-4 mr-2" />Datos Fiscales</TabsTrigger>
          <TabsTrigger value="payments"><BarChart3 className="h-4 w-4 mr-2" />Pagos</TabsTrigger>
          <TabsTrigger value="emails" data-testid="emails-tab"><Mail className="h-4 w-4 mr-2" />Emails</TabsTrigger>
        </TabsList>

        <TabsContent value="stores" className="space-y-4">

        <div className="flex gap-2">
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" />
              Nueva Tienda
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Crear Nueva Tienda</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre de la Tienda *</Label>
                  <Input
                    value={newStore.name}
                    onChange={(e) => setNewStore({ ...newStore, name: e.target.value })}
                    placeholder="Ej: Mi Tienda Madrid"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Plan</Label>
                  <Select 
                    value={newStore.plan} 
                    onValueChange={(val) => {
                      // Update limits based on selected plan
                      if (planLimits[val]) {
                        setNewStore(prev => ({
                          ...prev,
                          plan: val,
                          max_items: planLimits[val].max_items,
                          max_customers: planLimits[val].max_customers,
                          max_users: planLimits[val].max_users
                        }));
                      } else {
                        setNewStore({ ...newStore, plan: val });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trial">Trial (Prueba)</SelectItem>
                      <SelectItem value="basic">Basic</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {newStore.plan === "trial" ? (
                  <div className="col-span-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700 font-medium text-center">
                    Plan Trial: Clientes, Artículos y Usuarios <strong>ilimitados</strong> durante el período de prueba
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Máx. Usuarios</Label>
                      <Input
                        type="number"
                        value={newStore.max_users}
                        onChange={(e) => setNewStore({ ...newStore, max_users: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Máx. Artículos</Label>
                      <Input
                        type="number"
                        value={newStore.max_items}
                        onChange={(e) => setNewStore({ ...newStore, max_items: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Máx. Clientes</Label>
                      <Input
                        type="number"
                        value={newStore.max_customers}
                        onChange={(e) => setNewStore({ ...newStore, max_customers: parseInt(e.target.value) })}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email del Admin <span className="text-xs text-slate-500">(será su usuario de acceso)</span></Label>
                  <Input
                    type="email"
                    value={newStore.contact_email}
                    onChange={(e) => setNewStore({ ...newStore, contact_email: e.target.value })}
                    placeholder="admin@tienda.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={newStore.contact_phone}
                    onChange={(e) => setNewStore({ ...newStore, contact_phone: e.target.value })}
                    placeholder="+34 600 000 000"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Dirección</Label>
                <Input
                  value={newStore.address}
                  onChange={(e) => setNewStore({ ...newStore, address: e.target.value })}
                  placeholder="Calle Principal, 123"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateStore}>
                Crear Tienda
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <Button variant="outline" onClick={loadStores} disabled={loading} data-testid="refresh-stores-btn">
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
        </Button>
        {testStores.length > 0 && (
          <Button
            variant="destructive"
            onClick={() => setShowDeleteAllTest(true)}
            data-testid="delete-all-test-stores-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Eliminar todas las tiendas TEST ({testStores.length})
          </Button>
        )}

        {/* Simple confirm for TEST stores */}
        <Dialog open={!!deleteTestTarget} onOpenChange={(open) => { if (!open) setDeleteTestTarget(null); }}>
          <DialogContent className="max-w-md" data-testid="delete-test-store-dialog">
            <DialogHeader>
              <DialogTitle>Eliminar tienda de prueba</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600 py-2">
              ¿Eliminar la tienda de prueba «<strong>{deleteTestTarget?.name}</strong>»? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteTestTarget(null)} data-testid="cancel-delete-test-btn">
                Cancelar
              </Button>
              <Button
                variant="destructive"
                disabled={deletingTest}
                onClick={confirmDeleteTestStore}
                data-testid="confirm-delete-test-btn"
              >
                {deletingTest ? "Eliminando..." : "Eliminar"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Batch delete ALL test stores */}
        <Dialog open={showDeleteAllTest} onOpenChange={setShowDeleteAllTest}>
          <DialogContent className="max-w-md" data-testid="delete-all-test-dialog">
            <DialogHeader>
              <DialogTitle>Eliminar todas las tiendas TEST</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-sm text-slate-600">
                Se eliminarán <strong>{testStores.length}</strong> tiendas de prueba con todos sus datos. Esta acción no se puede deshacer.
              </p>
              <div className="max-h-48 overflow-y-auto border rounded-lg p-3 space-y-1" data-testid="test-stores-list">
                {testStores.map((s) => (
                  <p key={s.store_id} className="text-xs text-slate-500 font-mono truncate">
                    #{s.store_id} · {s.name}
                  </p>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDeleteAllTest(false)} data-testid="cancel-delete-all-test-btn">
                Cancelar
              </Button>
              <Button
                variant="destructive"
                disabled={deletingAllTest}
                onClick={handleDeleteAllTestStores}
                data-testid="confirm-delete-all-test-btn"
              >
                {deletingAllTest ? "Eliminando..." : `Eliminar ${testStores.length} tiendas`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stores Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stores.map((store) => {
          const stats = storeStats[store.store_id] || {};
          return (
            <Card key={store.store_id} className={darkMode ? 'bg-[#1a1a1a] border-[#333]' : ''}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Store className="h-5 w-5" />
                      {store.name}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      ID: {store.store_id}
                    </CardDescription>
                    {store.admin_email && (
                      <p
                        className="text-xs text-slate-500 flex items-center gap-1 mt-1 max-w-[190px] truncate"
                        title={store.admin_email}
                        data-testid={`store-admin-email-${store.store_id}`}
                      >
                        <Mail className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{store.admin_email}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    {getPlanBadge(store)}
                    {getStatusBadge(store.status)}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-500" />
                      <div>
                        <p className="text-xs text-slate-500">Clientes</p>
                        <p className="font-semibold">{stats.customers || 0}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-purple-500" />
                      <div>
                        <p className="text-xs text-slate-500">Artículos</p>
                        <p className="font-semibold">{stats.items || 0}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="h-4 w-4 text-green-500" />
                      <div>
                        <p className="text-xs text-slate-500">Alquileres</p>
                        <p className="font-semibold">{stats.rentals || 0}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-orange-500" />
                      <div>
                        <p className="text-xs text-slate-500">Usuarios</p>
                        <p className="font-semibold">{stats.users || 0}</p>
                      </div>
                    </div>
                  </div>

                  {/* Limits */}
                  <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                    <p className="text-xs text-slate-500 mb-2">Límites del Plan</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span>Usuarios:</span>
                        <span className="font-medium">
                          {stats.users || 0}/{store.settings?.max_users >= 999 ? "∞" : store.settings?.max_users}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Artículos:</span>
                        <span className="font-medium">
                          {stats.items || 0}/{store.settings?.max_items >= 999999 ? "∞" : store.settings?.max_items}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Clientes:</span>
                        <span className="font-medium">
                          {stats.customers || 0}/{store.settings?.max_customers >= 999999 ? "∞" : store.settings?.max_customers}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Trial status */}
                  {store.trial_end_date && (
                    <div className="pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center gap-2">
                      <CalendarClock className={`h-4 w-4 ${store.subscription_state === 'trial_expired' ? 'text-red-500' : 'text-amber-500'}`} />
                      <div className="text-xs" data-testid={`store-trial-info-${store.store_id}`}>
                        {store.subscription_state === 'trial_expired' ? (
                          <span className="font-semibold text-red-600">
                            Prueba caducada (hace {Math.abs(store.trial_days_remaining || 0)}d)
                          </span>
                        ) : (
                          <span className="font-semibold text-amber-600">
                            Prueba: {store.trial_days_remaining}d restantes
                          </span>
                        )}
                        <span className="text-slate-500 ml-1">
                          · fin {new Date(store.trial_end_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Subscription renewal */}
                  {store.plan_expires_at && (
                    <div className="pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center gap-2">
                      <CalendarClock className="h-4 w-4 text-indigo-500" />
                      <div className="text-xs">
                        <span className="text-slate-500">Renovación: </span>
                        <span className="font-semibold text-indigo-600">
                          {new Date(store.plan_expires_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                        {store.subscription_cancel_at_period_end && (
                          <Badge className="ml-2 text-[10px] bg-amber-100 text-amber-700 border-amber-300">Cancela</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="space-y-2 pt-2">
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => navigate(`/tiendas/${store.store_id}/ajustes`)}
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        Configurar
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleImpersonate(store.store_id)}
                      >
                        <TrendingUp className="h-3 w-3 mr-1" />
                        Acceder
                      </Button>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        variant={store.status === 'active' ? 'destructive' : 'default'}
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleToggleStatus(store.store_id, store.status)}
                      >
                        {store.status === 'active' ? 'Desactivar' : 'Activar'}
                      </Button>
                      {!store.is_system_store && (
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleDeleteStore(store.store_id, store.name, store.is_system_store)}
                        >
                          Eliminar
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty state */}
      {stores.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Store className="h-12 w-12 mx-auto mb-4 text-slate-600" />
            <h3 className="text-lg font-semibold mb-2">No hay tiendas registradas</h3>
            <p className="text-slate-500 mb-4">Crea tu primera tienda para comenzar</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nueva Tienda
            </Button>
          </CardContent>
        </Card>
      )}

        </TabsContent>

        {/* ==================== DATOS FISCALES ==================== */}
        <TabsContent value="fiscal" className="space-y-6">
          {/* Datos Fiscales del Emisor */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Datos Fiscales del Emisor
              </CardTitle>
              <CardDescription>
                Estos datos aparecerán en todas las facturas generadas (sección EMISOR).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
                <div className="space-y-2">
                  <Label>Razón Social</Label>
                  <Input
                    value={fiscalData.company_name || ""}
                    onChange={(e) => setFiscalData({ ...fiscalData, company_name: e.target.value })}
                    placeholder="Mi Empresa S.L."
                    data-testid="fiscal-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>CIF / NIF</Label>
                  <Input
                    value={fiscalData.cif || ""}
                    onChange={(e) => setFiscalData({ ...fiscalData, cif: e.target.value })}
                    placeholder="B12345678"
                    data-testid="fiscal-cif"
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label>Dirección Fiscal</Label>
                  <Input
                    value={fiscalData.address || ""}
                    onChange={(e) => setFiscalData({ ...fiscalData, address: e.target.value })}
                    placeholder="Calle Ejemplo 123, 28001 Madrid, España"
                    data-testid="fiscal-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email de Facturación</Label>
                  <Input
                    type="email"
                    value={fiscalData.email || ""}
                    onChange={(e) => setFiscalData({ ...fiscalData, email: e.target.value })}
                    placeholder="facturacion@empresa.es"
                    data-testid="fiscal-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={fiscalData.phone || ""}
                    onChange={(e) => setFiscalData({ ...fiscalData, phone: e.target.value })}
                    placeholder="+34 900 123 456"
                    data-testid="fiscal-phone"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Apariencia de Factura */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Apariencia de Factura
              </CardTitle>
              <CardDescription>
                Personaliza el aspecto visual de las facturas PDF que Stripe envia a tus clientes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Brand Color */}
              <div className="space-y-2 max-w-sm">
                <Label>Color de Marca</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={fiscalData.brand_color || "#1e3a8a"}
                    onChange={(e) => setFiscalData({ ...fiscalData, brand_color: e.target.value })}
                    className="w-12 h-10 rounded-lg border cursor-pointer"
                    data-testid="brand-color-picker"
                  />
                  <Input
                    value={fiscalData.brand_color || "#1e3a8a"}
                    onChange={(e) => setFiscalData({ ...fiscalData, brand_color: e.target.value })}
                    placeholder="#1e3a8a"
                    className="w-32 font-mono text-sm"
                    data-testid="brand-color-input"
                  />
                  <div className="flex-1 h-10 rounded-lg border" style={{ backgroundColor: fiscalData.brand_color || "#1e3a8a" }} />
                </div>
                <p className="text-xs text-muted-foreground">Se usara en botones y bordes de la factura PDF de Stripe.</p>
              </div>

              {/* Invoice Logo */}
              <div className="space-y-2">
                <Label>Logo de Factura</Label>
                <div className="flex items-center gap-4">
                  {fiscalData.invoice_logo ? (
                    <div className="flex items-center gap-4">
                      <img src={fiscalData.invoice_logo} alt="Logo factura" className="h-14 max-w-[180px] object-contain rounded border p-2 bg-white" data-testid="invoice-logo-preview" />
                      <Button variant="outline" size="sm" onClick={() => setFiscalData({ ...fiscalData, invoice_logo: null })} data-testid="remove-invoice-logo">Quitar</Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Sin logo configurado</p>
                  )}
                  <label className="cursor-pointer">
                    <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleInvoiceLogoUpload} className="hidden" data-testid="invoice-logo-upload" />
                    <Button variant="outline" size="sm" asChild disabled={uploadingInvoiceLogo}>
                      <span>{uploadingInvoiceLogo ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}{fiscalData.invoice_logo ? "Cambiar" : "Subir"} Logo</span>
                    </Button>
                  </label>
                </div>
                <p className="text-xs text-muted-foreground">JPG o PNG. Max 2MB. Stripe recomienda fondos transparentes. Se sube automaticamente a Stripe.</p>
              </div>

              {/* Invoice Prefix */}
              <div className="space-y-2 max-w-md">
                <Label>Prefijo de Factura (Stripe)</Label>
                <Input
                  value={fiscalData.invoice_prefix || ""}
                  onChange={(e) => setFiscalData({ ...fiscalData, invoice_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12) })}
                  placeholder="Ej: ALF, SKF, SKIFLOW"
                  maxLength={12}
                  data-testid="invoice-prefix"
                />
                <p className="text-xs text-muted-foreground">Prefijo para la numeracion de facturas en Stripe (max 12 caracteres). Las facturas seran: PREFIJO-0001, PREFIJO-0002, etc.</p>
              </div>

              {/* Invoice Footer */}
              <div className="space-y-2 max-w-2xl">
                <Label>Pie de Pagina / Notas Legales</Label>
                <textarea
                  value={fiscalData.invoice_footer || ""}
                  onChange={(e) => setFiscalData({ ...fiscalData, invoice_footer: e.target.value })}
                  placeholder="Registro Mercantil de Madrid, Tomo X, Folio Y, Hoja Z..."
                  className="w-full min-h-[100px] p-3 rounded-lg border bg-background text-sm resize-y"
                  data-testid="invoice-footer"
                />
                <p className="text-xs text-muted-foreground">Este texto aparecera al final de todas las facturas PDF generadas por Stripe.</p>
              </div>
            </CardContent>
          </Card>

          {/* Save button */}
          <div className="flex justify-end">
            <Button onClick={saveFiscalData} disabled={savingFiscal} size="lg" data-testid="save-fiscal-btn">
              {savingFiscal ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar Todo
            </Button>
          </div>
        </TabsContent>

        {/* ==================== PAGOS / ESTADÍSTICAS ==================== */}
        <TabsContent value="payments" className="space-y-6">
          {loadingPayments ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <p className="text-sm text-slate-500">Ingresos Totales</p>
                    <p className="text-2xl font-black text-emerald-600">
                      {(paymentsSummary.total_revenue || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                    </p>
                    <p className="text-xs text-slate-600">IVA incluido</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <p className="text-sm text-slate-500">Base Imponible</p>
                    <p className="text-2xl font-black">
                      {(paymentsSummary.total_base || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <p className="text-sm text-slate-500">IVA Recaudado</p>
                    <p className="text-2xl font-black text-blue-600">
                      {(paymentsSummary.total_iva || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <p className="text-sm text-slate-500">Pagos Totales</p>
                    <p className="text-2xl font-black">{paymentsSummary.total_payments || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly stats */}
              {monthlyStats.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Ingresos Mensuales
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Mes</TableHead>
                          <TableHead className="text-right">Pagos</TableHead>
                          <TableHead className="text-right">Base</TableHead>
                          <TableHead className="text-right">IVA</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {monthlyStats.map((m) => (
                          <TableRow key={m._id}>
                            <TableCell className="font-medium">{m._id}</TableCell>
                            <TableCell className="text-right">{m.count}</TableCell>
                            <TableCell className="text-right">{m.total_base?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</TableCell>
                            <TableCell className="text-right">{m.total_iva?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</TableCell>
                            <TableCell className="text-right font-bold">{m.total_with_iva?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* All payments table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Historial de Pagos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {allPayments.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No hay pagos registrados</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Factura</TableHead>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Tienda</TableHead>
                          <TableHead>Plan</TableHead>
                          <TableHead className="text-right">Base</TableHead>
                          <TableHead className="text-right">IVA</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead>Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allPayments.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell className="font-mono text-xs">{p.invoice_number || '-'}</TableCell>
                            <TableCell>{p.date ? new Date(p.date).toLocaleDateString('es-ES') : '-'}</TableCell>
                            <TableCell className="font-medium">{p.store_name}</TableCell>
                            <TableCell><Badge variant="outline" className="capitalize">{p.plan || p.plan_name}</Badge></TableCell>
                            <TableCell className="text-right">{(p.amount || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</TableCell>
                            <TableCell className="text-right">{(p.iva_amount || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</TableCell>
                            <TableCell className="text-right font-bold">{(p.total_with_iva || p.amount || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</TableCell>
                            <TableCell>
                              <Badge className={p.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                                {p.status === 'paid' ? 'Pagado' : p.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ==================== EMAILS TAB ==================== */}
        <TabsContent value="emails" className="space-y-6">
          {loadingEmails ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : editingTemplate ? (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">{editingTemplate.name}</h3>
                  <p className="text-sm text-muted-foreground">Alias: <code className="bg-muted px-2 py-0.5 rounded text-xs">{editingTemplate.alias}</code></p>
                </div>
                <Button variant="outline" onClick={() => setEditingTemplate(null)} data-testid="back-to-templates">Volver a la lista</Button>
              </div>

              {/* Variables disponibles */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2"><Code className="h-4 w-4" />Variables disponibles</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {(editingTemplate.variables || []).map(v => (
                      <Badge key={v} variant="secondary" className="font-mono text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground" onClick={() => {
                        navigator.clipboard.writeText(`{{${v}}}`);
                        toast.success(`{{${v}}} copiado`);
                      }} data-testid={`var-${v}`}>{`{{${v}}}`}</Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Haz clic en una variable para copiarla al portapapeles</p>
                </CardContent>
              </Card>

              {/* Editor de asunto */}
              <div className="space-y-2">
                <Label>Asunto del email</Label>
                <Input value={editSubject} onChange={e => setEditSubject(e.target.value)} data-testid="edit-subject" />
              </div>

              {/* Estado activo */}
              <div className="flex items-center gap-3">
                <Label>Estado:</Label>
                <Button variant={editActive ? "default" : "outline"} size="sm" onClick={() => setEditActive(!editActive)} data-testid="toggle-active">
                  {editActive ? <><Eye className="h-4 w-4 mr-1" />Activo</> : <><EyeOff className="h-4 w-4 mr-1" />Desactivado</>}
                </Button>
              </div>

              {/* Logo upload */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2"><Image className="h-4 w-4" />Logo de Cabecera</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    {templateLogo ? (
                      <div className="flex items-center gap-4">
                        <img src={templateLogo} alt="Logo cabecera" className="h-12 max-w-[160px] object-contain rounded border p-1 bg-white" data-testid="logo-preview" />
                        <Button variant="outline" size="sm" onClick={() => setTemplateLogo(null)} data-testid="remove-logo-btn">Quitar</Button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Sin logo configurado</p>
                    )}
                    <label className="cursor-pointer">
                      <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleLogoUpload} className="hidden" data-testid="logo-upload-input" />
                      <Button variant="outline" size="sm" asChild disabled={uploadingLogo}>
                        <span>{uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}{templateLogo ? "Cambiar" : "Subir"} Logo</span>
                      </Button>
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">JPG o PNG. Max 2MB. Se mostrara en la cabecera del email.</p>
                </CardContent>
              </Card>

              {/* Editor WYSIWYG */}
              <div className="space-y-2">
                <Label>Cuerpo del mensaje</Label>
                <div className="border rounded-lg overflow-hidden bg-white" data-testid="edit-body">
                  <ReactQuill
                    theme="snow"
                    value={editBody}
                    onChange={setEditBody}
                    modules={QUILL_MODULES}
                    style={{ minHeight: "350px" }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">Usa los botones de la barra para dar formato. Las variables {"{{nombre}}"} se pueden escribir directamente en el texto.</p>
              </div>

              {/* Preview */}
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-sm">Vista previa del email</CardTitle></CardHeader>
                <CardContent>
                  <div className="border rounded-lg p-6 bg-white max-w-[600px] mx-auto" data-testid="email-preview">
                    {templateLogo && (
                      <div style={{ textAlign: "center", marginBottom: "24px" }}>
                        <img src={templateLogo} alt="Logo" style={{ maxHeight: "60px", maxWidth: "200px" }} />
                      </div>
                    )}
                    <div dangerouslySetInnerHTML={{ __html: editBody }} />
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex items-center gap-3 flex-wrap">
                <Button onClick={saveTemplate} disabled={savingTemplate} data-testid="save-template-btn">
                  {savingTemplate ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Guardar Template
                </Button>
                <div className="flex items-center gap-2 ml-auto">
                  <Input placeholder="Email para prueba" value={testEmail} onChange={e => setTestEmail(e.target.value)} className="w-64" data-testid="test-email-input" />
                  <Button variant="outline" onClick={sendTestEmail} disabled={sendingTest || !testEmail} data-testid="send-test-btn">
                    {sendingTest ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Enviar Prueba
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Templates list */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" />Gestion de Emails</CardTitle>
                  <CardDescription>Plantillas de correos transaccionales que se envian automaticamente</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Plantilla</TableHead>
                        <TableHead>Alias</TableHead>
                        <TableHead>Asunto</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {emailTemplates.map(tpl => (
                        <TableRow key={tpl.alias} data-testid={`template-row-${tpl.alias}`}>
                          <TableCell className="font-medium">{tpl.name}</TableCell>
                          <TableCell><code className="bg-muted px-2 py-0.5 rounded text-xs">{tpl.alias}</code></TableCell>
                          <TableCell className="max-w-xs truncate text-sm text-muted-foreground">{tpl.subject}</TableCell>
                          <TableCell>
                            {tpl.active !== false
                              ? <Badge className="bg-emerald-100 text-emerald-700">Activo</Badge>
                              : <Badge variant="secondary">Desactivado</Badge>}
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => openTemplateEditor(tpl)} data-testid={`edit-${tpl.alias}`}>
                              Editar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Email send history (full, paginated) */}
              <Card>
                  <CardHeader>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Send className="h-4 w-4" />
                        Historial de envíos
                        <Badge variant="secondary" data-testid="email-logs-total-badge">{emailLogsTotal}</Badge>
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Buscar por destinatario..."
                          value={emailLogsSearch}
                          onChange={(e) => setEmailLogsSearch(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") loadEmailLogs(1, emailLogsSearch); }}
                          className="h-8 w-56 text-sm"
                          data-testid="email-logs-search-input"
                        />
                        <Button variant="outline" size="sm" onClick={() => loadEmailLogs(1, emailLogsSearch)} data-testid="email-logs-search-btn">
                          Buscar
                        </Button>
                        {emailLogsSearch && (
                          <Button variant="ghost" size="sm" onClick={() => { setEmailLogsSearch(""); loadEmailLogs(1, ""); }} data-testid="email-logs-clear-btn">
                            Limpiar
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {emailLogs.length === 0 ? (
                      <p className="text-sm text-slate-500 py-6 text-center" data-testid="email-logs-empty">
                        {loadingEmailLogs ? "Cargando..." : "No hay envíos que coincidan."}
                      </p>
                    ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Template</TableHead>
                          <TableHead>Destinatario</TableHead>
                          <TableHead>Asunto</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead>Acción</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {emailLogs.map((log, i) => (
                          <TableRow key={log.id || log.sent_at || `log-${i}`}>
                            <TableCell className="text-sm">{log.sent_at ? new Date(log.sent_at).toLocaleString("es-ES") : "-"}</TableCell>
                            <TableCell><code className="text-xs">{log.template_alias}</code></TableCell>
                            <TableCell className="text-sm">{log.recipient}</TableCell>
                            <TableCell className="text-sm max-w-xs truncate">{log.subject}</TableCell>
                            <TableCell>
                              {log.status === "sent"
                                ? <Badge className="bg-emerald-100 text-emerald-700" title={log.provider_message_id ? `MessageID: ${log.provider_message_id}` : undefined} data-testid={`email-log-sent-badge-${i}`}>Enviado</Badge>
                                : (
                                  <Badge
                                    variant="destructive"
                                    className="cursor-help"
                                    title={log.error_detail || log.error || "Sin detalle del error"}
                                    data-testid={`email-log-error-badge-${i}`}
                                  >
                                    Error
                                  </Badge>
                                )}
                              {log.status !== "sent" && (log.error_detail || log.error) && (
                                <p className="text-[11px] text-red-500 mt-1 max-w-[240px] truncate" title={log.error_detail || log.error} data-testid={`email-log-error-detail-${i}`}>
                                  {log.error_detail || log.error}
                                </p>
                              )}
                            </TableCell>
                            <TableCell>
                              {log.log_id ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={resendingLogId === log.log_id}
                                  onClick={() => handleResendEmail(log)}
                                  className="text-xs gap-1"
                                >
                                  <Send className="h-3 w-3" />
                                  {resendingLogId === log.log_id ? "Enviando..." : "Reenviar"}
                                </Button>
                              ) : (
                                <span className="text-xs text-slate-600">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    )}
                    <div className="flex items-center justify-between pt-4" data-testid="email-logs-pagination">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={emailLogsPage <= 1 || loadingEmailLogs}
                        onClick={() => loadEmailLogs(emailLogsPage - 1)}
                        data-testid="email-logs-prev-btn"
                      >
                        ← Anterior
                      </Button>
                      <span className="text-xs text-slate-500">
                        Página {emailLogsPage} de {emailLogsTotalPages} · {emailLogsTotal} envíos en total
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={emailLogsPage >= emailLogsTotalPages || loadingEmailLogs}
                        onClick={() => loadEmailLogs(emailLogsPage + 1)}
                        data-testid="email-logs-next-btn"
                      >
                        Siguiente →
                      </Button>
                    </div>
                  </CardContent>
                </Card>
            </>
          )}
        </TabsContent>

      </Tabs>
    </div>
  );
}
